import React, { useState } from 'react';
import ClinicInfo from '../common/ClinicInfo';
import Header from '../common/Header';
import { BookingData } from '../../types/BookingTypes';

interface PatientDetailsFormProps {
  bookingData: BookingData;
  updateBookingData: (field: keyof BookingData, value: string) => void;
  onNext: () => void;
  onChangePatientType: () => void;
  onChangeDateTime: () => void;
}

const PatientDetailsForm: React.FC<PatientDetailsFormProps> = ({
  bookingData,
  updateBookingData,
  onNext,
  onChangePatientType,
  onChangeDateTime
}) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%', height: '55px', borderRadius: '8px',
    border: `1px solid ${hasError ? 'rgb(142,31,11)' : '#E8E8E8'}`,
    backgroundColor: hasError ? 'rgb(254,233,232)' : 'white',
    fontFamily: 'Manrope, sans-serif', fontSize: '16px', fontWeight: 500
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // First Name validation
    if (!bookingData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    } else if (bookingData.firstName.length > 100) {
      newErrors.firstName = 'First Name must not exceed 100 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(bookingData.firstName)) {
      newErrors.firstName = 'First Name can only contain letters and spaces';
    }

    // Last Name validation
    if (!bookingData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    } else if (bookingData.lastName.length > 100) {
      newErrors.lastName = 'Last Name must not exceed 100 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(bookingData.lastName)) {
      newErrors.lastName = 'Last Name can only contain letters and spaces';
    }

    // Middle Name validation (optional but must follow rules if provided)
    if (bookingData.middleName && bookingData.middleName.trim()) {
      if (bookingData.middleName.length > 100) {
        newErrors.middleName = 'Middle Name must not exceed 100 characters';
      } else if (!/^[a-zA-Z\s]+$/.test(bookingData.middleName)) {
        newErrors.middleName = 'Middle Name can only contain letters and spaces';
      }
    }

    // Mobile Number validation
    if (!bookingData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile Number is required';
    } else {
      // Remove all non-digit characters except leading +
      const normalized = bookingData.mobileNumber.replace(/[^0-9+]/g, '');
      // p73: must not exceed 13 chars total (+ and up to 12 digits, e.g. +639171234567).
      if (!/^\+[0-9]{10,12}$/.test(normalized)) {
        newErrors.mobileNumber = 'Invalid format. Use +639171234567';
      }
    }

    // Occupation validation (optional but must follow rules if provided)
    if (bookingData.occupation && bookingData.occupation.trim() && bookingData.occupation.length > 200) {
      newErrors.occupation = 'Occupation must not exceed 200 characters';
    }

    // Email validation (required for all patient types)
    if (!bookingData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email Address is required';
    } else if (bookingData.emailAddress.length > 255) {
      newErrors.emailAddress = 'Email must not exceed 255 characters';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    if (bookingData.patientType === 'New') {
      // Gender validation
      if (!bookingData.gender) {
        newErrors.gender = 'Gender is required';
      }

      // Civil Status validation
      if (!bookingData.civilStatus.trim()) {
        newErrors.civilStatus = 'Civil Status is required';
      }

      // Date of Birth validation (MM/DD/YYYY)
      if (!bookingData.dateOfBirth) {
        newErrors.dateOfBirth = 'Date of Birth is required';
      } else {
        const parts = bookingData.dateOfBirth.split('/');
        if (parts.length !== 3 || parts[0].length !== 2 || parts[1].length !== 2 || parts[2].length !== 4) {
          newErrors.dateOfBirth = 'Use MM/DD/YYYY format';
        } else {
          const [mm, dd, yyyy] = parts.map(Number);
          const dob = new Date(yyyy, mm - 1, dd);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const oneHundredFiftyYearsAgo = new Date();
          oneHundredFiftyYearsAgo.setFullYear(today.getFullYear() - 150);

          if (isNaN(dob.getTime()) || dob.getMonth() !== mm - 1) {
            newErrors.dateOfBirth = 'Please enter a valid date';
          } else if (dob >= today) {
            newErrors.dateOfBirth = 'Date of birth must be before today';
          } else if (dob < oneHundredFiftyYearsAgo) {
            newErrors.dateOfBirth = 'Please enter a valid date of birth';
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />
      <div className="w-full flex flex-col items-center pt-[53px] pb-[50px]">
        <h1 className="text-[24px] font-bold text-cosmo-green mb-[45px] text-center tracking-[-0.48px] shrink-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Book your Appointment
        </h1>

        <div className="w-full max-w-[1241px] flex justify-between items-start px-4 lg:px-0">
          <div className="w-full lg:w-[673px]">
            {/* Clinic Info - Mobile Only */}
            <div className="lg:hidden mb-[53px] flex-shrink-0">
              <ClinicInfo />
            </div>

            <div>
              <div className="mb-[30px] flex-shrink-0 border-b border-[#dddddd] pb-[20px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[20px] font-bold text-[#242424] tracking-[-0.4px]" style={{ fontFamily: 'Manrope, sans-serif' }}>{bookingData.patientType} Patient</span>
                  <button onClick={onChangePatientType} className="text-[14px] font-semibold text-[#242424] underline tracking-[-0.28px]" style={{ fontFamily: 'Manrope, sans-serif' }}>Change</button>
                </div>
                <span className="text-[14px] font-normal text-[#242424] tracking-[-0.28px]" style={{ fontFamily: 'Manrope, sans-serif' }}>{bookingData.reason}</span>
              </div>
              
              <div className="mb-6 flex-shrink-0 border-b border-[#dddddd] pb-[20px]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[20px] font-bold text-[#242424] tracking-[-0.4px]" style={{ fontFamily: 'Manrope, sans-serif' }}>{bookingData.selectedDate}</span>
                  <button onClick={onChangeDateTime} className="text-[14px] font-semibold text-[#242424] underline tracking-[-0.28px]" style={{ fontFamily: 'Manrope, sans-serif' }}>Change</button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <img src="/images/clock-4.svg" alt="Clock" className="w-[18px] h-[18px]" />
                  <span className="text-[15px] font-medium text-[#242424] tracking-[-0.3px]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {bookingData.selectedTime || '12:00 PM'}
                  </span>
                </div>
              </div>
              
              <div className="mt-[40px]">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-[12px] mb-[22px]">
                  <div className="flex flex-col relative">
                    <input 
                      type="text" 
                      placeholder="First Name" 
                      value={bookingData.firstName} 
                      onChange={(e) => updateBookingData('firstName', e.target.value)} 
                      className={`px-[20px] ${errors.firstName ? 'border-red-500' : ''} placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]`}
                      style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.firstName) }}
                    />
                    {/* First Name is required for every patient type (see validateForm below) */}
                    <span className="absolute top-[2px] right-[10px] text-red-500 text-xs">*</span>
                    {errors.firstName && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.firstName}</span>}
                  </div>
                  <div className="flex flex-col relative">
                    <input 
                      type="text" 
                      placeholder="Middle Name" 
                      value={bookingData.middleName} 
                      onChange={(e) => updateBookingData('middleName', e.target.value)} 
                      className={`px-[20px] placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]`}
                      style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.middleName) }}
                    />
                    {errors.middleName && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.middleName}</span>}
                  </div>
                  <div className="flex flex-col relative">
                    <input 
                      type="text" 
                      placeholder="Last Name" 
                      value={bookingData.lastName} 
                      onChange={(e) => updateBookingData('lastName', e.target.value)} 
                      className={`px-[20px] placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]`}
                      style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.lastName) }}
                    />
                    {/* Last Name is required for every patient type (see validateForm below) */}
                    <span className="absolute top-[2px] right-[10px] text-red-500 text-xs">*</span>
                    {errors.lastName && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.lastName}</span>}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[12px] mb-[22px]">
                  <div className="flex flex-col relative">
                    <div className="relative">
                      <select 
                        value={bookingData.gender} 
                        onChange={(e) => updateBookingData('gender', e.target.value)} 
                        className={`focus:outline-none focus:border-cosmo-green tracking-[-0.32px] bg-white appearance-none cursor-pointer px-[20px] ${bookingData.gender ? 'text-[#242424]' : 'text-[#9f9f9f]'}`}
                        style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.gender) }}
                      >
                        <option value="" disabled hidden>Select Gender</option>
                        <option value="Male" className="text-[#242424]">Male</option>
                        <option value="Female" className="text-[#242424]">Female</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9L12 15L18 9" stroke="#979797" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    {/* Gender is only required for New patients (see validateForm below) */}
                    {bookingData.patientType === 'New' && <span className="absolute top-[2px] right-[10px] text-red-500 text-xs z-10">*</span>}
                    {errors.gender && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.gender}</span>}
                  </div>
                  <div className="flex flex-col relative">
                    <div className="relative">
                      <select 
                        value={bookingData.civilStatus} 
                        onChange={(e) => updateBookingData('civilStatus', e.target.value)} 
                        className={`focus:outline-none focus:border-cosmo-green tracking-[-0.32px] bg-white appearance-none cursor-pointer px-[20px] ${bookingData.civilStatus ? 'text-[#242424]' : 'text-[#9f9f9f]'}`}
                        style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.civilStatus) }}
                      >
                        <option value="" disabled hidden>Select Civil Status</option>
                        <option value="Single" className="text-[#242424]">Single</option>
                        <option value="Married" className="text-[#242424]">Married</option>
                        <option value="Divorced" className="text-[#242424]">Divorced</option>
                        <option value="Widowed" className="text-[#242424]">Widowed</option>
                        <option value="Separated" className="text-[#242424]">Separated</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9L12 15L18 9" stroke="#979797" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    {bookingData.patientType === 'New' && <span className="absolute top-[2px] right-[10px] text-red-500 text-xs z-10">*</span>}
                    {errors.civilStatus && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.civilStatus}</span>}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[12px] mb-[22px]">
                  <div className="flex flex-col relative">
                    <input 
                      type="text"
                      placeholder="MM/DD/YYYY (e.g., 05/15/1990)"
                      value={bookingData.dateOfBirth}
                      onChange={(e) => {
                        // p: rebuilding from the digits alone (instead of patching the raw
                        // string in place) means it doesn't matter whether the slashes came
                        // from auto-insertion or the user typing them — a manually-typed "/"
                        // used to survive alongside the auto-inserted one and produce "05//15",
                        // shifting/mangling everything typed after it.
                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 8); // MMDDYYYY
                        let val = digitsOnly;
                        if (digitsOnly.length > 4) {
                          val = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
                        } else if (digitsOnly.length > 2) {
                          val = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
                        }
                        updateBookingData('dateOfBirth', val);
                      }}
                      maxLength={10}
                      className={`px-[20px] text-[16px] h-[55px] font-medium placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]`}
                      style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.dateOfBirth) }}
                    />
                    {bookingData.patientType === 'New' && <span className="absolute top-[2px] right-[30px] text-red-500 text-xs z-10">*</span>}
                    {errors.dateOfBirth && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.dateOfBirth}</span>}
                  </div>
                  <div className="flex flex-col relative">
                    <input 
                      type="text" 
                      placeholder="Occupation" 
                      value={bookingData.occupation} 
                      onChange={(e) => updateBookingData('occupation', e.target.value)} 
                      className={`px-[20px] placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]`}
                      style={{ width: '100%', height: '55px', borderRadius: '8px', ...inputStyle(!!errors.occupation) }}
                    />
                    {errors.occupation && <span className="text-[13px] mt-1 block leading-tight" style={{ color: 'rgb(142,31,11)' }}>{errors.occupation}</span>}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[12px] mb-[22px]">
                  <div className="flex flex-col relative">
                    <input 
                      type="text" 
                      placeholder="Mobile Number"
                      value={bookingData.mobileNumber}
                      onChange={(e) => updateBookingData('mobileNumber', e.target.value)}
                      maxLength={13}
                      className="px-[20px] py-[16px] rounded-[8px] text-[16px] h-[55px] font-medium placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]"
                      style={{ fontFamily: 'Manrope, sans-serif', ...inputStyle(!!errors.mobileNumber) }}
                    />
                    <span className="absolute top-[2px] right-[10px] text-red-500 text-xs">*</span>
                    {errors.mobileNumber && <span className="text-[13px] mt-1 flex items-center gap-1 leading-tight" style={{ color: 'rgb(142,31,11)' }}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      {errors.mobileNumber}
                    </span>}
                  </div>
                  <div className="flex flex-col relative">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={bookingData.emailAddress}
                      onChange={(e) => updateBookingData('emailAddress', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleNext(); }}
                      className="px-[20px] py-[16px] rounded-[8px] text-[16px] h-[55px] font-medium placeholder:text-[#9f9f9f] focus:outline-none focus:border-cosmo-green tracking-[-0.32px]"
                      style={{ fontFamily: 'Manrope, sans-serif', ...inputStyle(!!errors.emailAddress) }}
                    />
                    <span className="absolute top-[2px] right-[10px] text-red-500 text-xs">*</span>
                    {errors.emailAddress && <span className="text-[13px] mt-1 flex items-center gap-1 leading-tight" style={{ color: 'rgb(142,31,11)' }}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      {errors.emailAddress}
                    </span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-[30px] flex justify-center lg:justify-start gap-[14px]">
                <button 
                  onClick={() => onChangeDateTime()} 
                  className="w-[256px] h-[55px] bg-[#f3f3f3] text-[#242424] rounded-[8px] text-[16px] font-semibold tracking-[-0.32px] hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Back
                </button>
                <button 
                  onClick={handleNext} 
                  className="w-[256px] h-[55px] bg-cosmo-green text-white rounded-[8px] text-[16px] font-semibold tracking-[-0.32px] hover:opacity-90 transition-colors flex items-center justify-center cursor-pointer"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Next
                </button>
              </div>
          </div>

          {/* Clinic Info - Desktop Only */}
          <div className="hidden lg:block w-[441px] flex-shrink-0">
            <ClinicInfo />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsForm;
